import { useEffect, useState } from 'react';
import type { StrategyStatus } from '../types/trade';
import { api } from '../services/api';

interface StrategyConfigProps {
  refreshInterval?: number;
}

interface StrategyParams {
  [key: string]: {
    value: number | string | boolean;
    type: 'number' | 'string' | 'boolean';
    label: string;
    min?: number;
    max?: number;
    step?: number;
  };
}

const STRATEGY_PARAMS: Record<string, StrategyParams> = {
  spread_maintainer: {
    target_spread: { value: 0.5, type: 'number', label: 'Target Spread %', min: 0.1, max: 2.0, step: 0.1 },
    refresh_interval: { value: 30, type: 'number', label: 'Refresh Interval (s)', min: 10, max: 120, step: 5 },
    min_depth: { value: 500, type: 'number', label: 'Min Depth ($)', min: 100, max: 2000, step: 100 },
  },
  volume_generator: {
    target_volume: { value: 1000, type: 'number', label: 'Target Volume ($)', min: 100, max: 10000, step: 100 },
    trade_size_min: { value: 50, type: 'number', label: 'Min Trade Size ($)', min: 10, max: 500, step: 10 },
    trade_size_max: { value: 200, type: 'number', label: 'Max Trade Size ($)', min: 50, max: 1000, step: 50 },
  },
  adaptive_spread: {
    volatility_threshold: { value: 0.02, type: 'number', label: 'Volatility Threshold', min: 0.01, max: 0.1, step: 0.005 },
    adaptive_enabled: { value: true, type: 'boolean', label: 'Adaptive Mode' },
  },
};

export function StrategyConfig({ refreshInterval = 10000 }: StrategyConfigProps) {
  const [strategies, setStrategies] = useState<StrategyStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedStrategy, setExpandedStrategy] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    loadStrategies();
    const interval = setInterval(loadStrategies, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const loadStrategies = async () => {
    try {
      const data = await api.getStrategies();
      setStrategies(data);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load strategies');
      setLoading(false);
    }
  };

  const toggleStrategy = async (strategyId: string, currentState: boolean) => {
    try {
      setSaving(strategyId);
      await api.toggleStrategy(strategyId, !currentState);
      await loadStrategies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle strategy');
    } finally {
      setSaving(null);
    }
  };

  const updateParam = async (strategyId: string, paramKey: string, value: unknown) => {
    try {
      setSaving(strategyId);
      await api.updateStrategyParams(strategyId, { [paramKey]: value });
      await loadStrategies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update parameter');
    } finally {
      setSaving(null);
    }
  };

  const formatMetric = (value: number, type: string) => {
    if (type === 'volume' || type === 'pnl') {
      return `$${value.toFixed(2)}`;
    }
    return value.toString();
  };

  if (loading && strategies.length === 0) {
    return <div className="strategy-config loading">Loading strategies...</div>;
  }

  if (error) {
    return <div className="strategy-config error">{error}</div>;
  }

  return (
    <div className="strategy-config">
      <div className="config-header">
        <h3>Strategy Configuration</h3>
        <button onClick={loadStrategies} className="refresh-btn" title="Refresh">
          ↻
        </button>
      </div>

      <div className="strategies-list">
        {strategies.map((strategy) => (
          <div
            key={strategy.id}
            className={`strategy-card ${strategy.running ? 'running' : 'stopped'}`}
          >
            <div className="strategy-header" onClick={() => setExpandedStrategy(
              expandedStrategy === strategy.id ? null : strategy.id
            )}>
              <div className="strategy-info">
                <span className={`status-indicator ${strategy.running ? 'active' : 'inactive'}`} />
                <span className="strategy-name">{strategy.name}</span>
              </div>
              <div className="strategy-controls">
                <button
                  className={`toggle-btn ${strategy.running ? 'stop' : 'start'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleStrategy(strategy.id, strategy.running);
                  }}
                  disabled={saving === strategy.id}
                >
                  {saving === strategy.id ? '...' : strategy.running ? 'Stop' : 'Start'}
                </button>
                <span className="expand-icon">{expandedStrategy === strategy.id ? '▼' : '▶'}</span>
              </div>
            </div>

            {strategy.running && (
              <div className="strategy-metrics">
                <span>Trades: {strategy.metrics.tradesExecuted}</span>
                <span>Volume: {formatMetric(strategy.metrics.volumeGenerated, 'volume')}</span>
                <span className={strategy.metrics.pnl >= 0 ? 'positive' : 'negative'}>
                  P&L: {formatMetric(strategy.metrics.pnl, 'pnl')}
                </span>
              </div>
            )}

            {expandedStrategy === strategy.id && STRATEGY_PARAMS[strategy.id] && (
              <div className="strategy-params">
                <h4>Parameters</h4>
                {Object.entries(STRATEGY_PARAMS[strategy.id]).map(([key, param]) => (
                  <div key={key} className="param-row">
                    <label>{param.label}</label>
                    {param.type === 'boolean' ? (
                      <input
                        type="checkbox"
                        checked={param.value as boolean}
                        onChange={(e) => updateParam(strategy.id, key, e.target.checked)}
                        disabled={saving === strategy.id}
                      />
                    ) : param.type === 'number' ? (
                      <input
                        type="number"
                        value={param.value as number}
                        min={param.min}
                        max={param.max}
                        step={param.step}
                        onChange={(e) => updateParam(strategy.id, key, parseFloat(e.target.value))}
                        disabled={saving === strategy.id}
                      />
                    ) : (
                      <input
                        type="text"
                        value={param.value as string}
                        onChange={(e) => updateParam(strategy.id, key, e.target.value)}
                        disabled={saving === strategy.id}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {strategy.lastAction && (
              <div className="last-action">
                Last: {strategy.lastAction} at {new Date(strategy.lastActionTime).toLocaleTimeString()}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
